import { useState, useEffect } from 'react';
import { MobileSelect } from '@/components/ui';
import { SupplierSelect } from './SupplierSelect';
import { useUnassignedReceipts } from '../hooks';
import { useProjectOptions } from '@/hooks';
import { getReceiptUrl } from '../services/receiptService';
import { OCR_STATUS_CONFIG } from '../types';

export function UnassignedReceiptsList() {
  const { receipts, isLoading, error, assignReceipt, dismissReceipt, refetch } =
    useUnassignedReceipts();

  const { projects } = useProjectOptions(['planned', 'in_progress']);

  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [selectedProjects, setSelectedProjects] = useState<Record<string, string>>({});
  const [selectedSuppliers, setSelectedSuppliers] = useState<Record<string, string>>({});
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  // Load signed URLs
  useEffect(() => {
    const loadUrls = async () => {
      for (const receipt of receipts) {
        // Skip PDFs
        if (receipt.filePath.toLowerCase().endsWith('.pdf')) {
          continue;
        }

        if (!signedUrls[receipt.id]) {
          try {
            const url = await getReceiptUrl(receipt.filePath);
            setSignedUrls((prev) => ({ ...prev, [receipt.id]: url }));
          } catch (error) {
            console.error(`Error loading URL for receipt ${receipt.id}:`, error);
          }
        }
      }
    };
    loadUrls();
  }, [receipts, signedUrls]);

  // Initialize selected suppliers from OCR-detected vendor names
  useEffect(() => {
    // This could be enhanced to match vendor names to existing suppliers
  }, [receipts]);

  const handleAssign = async (receiptId: string) => {
    const projectId = selectedProjects[receiptId];
    const supplierId = selectedSuppliers[receiptId] || null;

    if (!projectId) return;

    setProcessingIds((prev) => new Set(prev).add(receiptId));
    try {
      await assignReceipt(receiptId, projectId, supplierId);
    } catch (error) {
      console.error('Error assigning receipt:', error);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(receiptId);
        return next;
      });
    }
  };

  const handleDismiss = async (receiptId: string) => {
    setProcessingIds((prev) => new Set(prev).add(receiptId));
    try {
      await dismissReceipt(receiptId);
    } catch (error) {
      console.error('Error dismissing receipt:', error);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(receiptId);
        return next;
      });
    }
  };

  const isPdf = (filePath: string): boolean => {
    return filePath.toLowerCase().endsWith('.pdf');
  };

  const formatCurrency = (amount: number | null, currency: string | null): string => {
    if (amount === null) return '—';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency || 'CAD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        <p>{error}</p>
        <button
          onClick={refetch}
          className="mt-2 text-sm underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (receipts.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">All receipts have been assigned!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-600">
          {receipts.length} receipt{receipts.length !== 1 ? 's' : ''} pending
          assignment
        </p>
        <button
          onClick={refetch}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {receipts.map((receipt) => {
          const isProcessing = processingIds.has(receipt.id);
          const ocrConfig = OCR_STATUS_CONFIG[receipt.ocrStatus];

          return (
            <div
              key={receipt.id}
              className="bg-white border border-secondary-200 rounded-lg p-4 flex gap-4"
            >
              {/* Receipt thumbnail */}
              <div className="w-24 h-24 flex-shrink-0 bg-secondary-100 rounded-lg overflow-hidden">
                {isPdf(receipt.filePath) ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-secondary-400">
                    <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4z" />
                    </svg>
                    <span className="text-xs">PDF</span>
                  </div>
                ) : signedUrls[receipt.id] ? (
                  <img
                    src={signedUrls[receipt.id]}
                    alt="Receipt"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-secondary-400 animate-pulse"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Receipt details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-secondary-900 truncate">
                      {receipt.vendorNameRaw || 'Receipt'}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-sm text-secondary-500">
                        {new Date(receipt.uploadedAt).toLocaleDateString()}
                      </span>
                      {receipt.totalAmount !== null && (
                        <span className="text-sm font-medium text-secondary-700">
                          {formatCurrency(receipt.totalAmount, receipt.currency)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* OCR Status Badge */}
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${ocrConfig.color}`}
                  >
                    {ocrConfig.label}
                  </span>
                </div>

                {/* Selectors and actions */}
                <div className="mt-3 space-y-3">
                  {/* Project selector */}
                  <MobileSelect
                    label="Project"
                    value={selectedProjects[receipt.id] || ''}
                    onChange={(value) =>
                      setSelectedProjects((prev) => ({
                        ...prev,
                        [receipt.id]: value,
                      }))
                    }
                    disabled={isProcessing}
                    placeholder="Select a project..."
                    options={projects.map((project) => ({
                      value: project.id,
                      label: project.name,
                      sublabel: project.customerName || undefined,
                    }))}
                  />

                  {/* Supplier selector */}
                  <SupplierSelect
                    value={selectedSuppliers[receipt.id] || null}
                    onChange={(supplierId) =>
                      setSelectedSuppliers((prev) => ({
                        ...prev,
                        [receipt.id]: supplierId || '',
                      }))
                    }
                    disabled={isProcessing}
                  />

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAssign(receipt.id)}
                      disabled={!selectedProjects[receipt.id] || isProcessing}
                      className="flex-1 sm:flex-none px-4 py-3 md:py-2 bg-primary-600 text-white text-base md:text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isProcessing ? 'Saving...' : 'Assign'}
                    </button>

                    <button
                      onClick={() => handleDismiss(receipt.id)}
                      disabled={isProcessing}
                      className="flex-1 sm:flex-none px-4 py-3 md:py-2 border border-secondary-300 text-secondary-700 text-base md:text-sm font-medium rounded-lg hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Skip
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}